import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  alpha,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: number;
  trendDirection?: 'up' | 'down';
  icon?: React.ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  onClick?: () => void;
  loading?: boolean;
}

const colorMap = {
  primary: { main: '#2563eb', light: '#eff6ff', dark: '#1e40af' },
  success: { main: '#10b981', light: '#ecfdf5', dark: '#047857' },
  warning: { main: '#f59e0b', light: '#fef3c7', dark: '#d97706' },
  error: { main: '#ef4444', light: '#fef2f2', dark: '#dc2626' },
  info: { main: '#3b82f6', light: '#eff6ff', dark: '#1d4ed8' },
};

export default function StatCard({
  title,
  value,
  subtitle,
  trend,
  trendDirection = 'up',
  icon,
  color = 'primary',
  onClick,
  loading = false,
}: StatCardProps) {
  const colors = colorMap[color];

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        background: `linear-gradient(135deg, ${colors.light} 0%, ${colors.light} 100%)`,
        border: `1px solid ${alpha(colors.main, 0.2)}`,
        '&:hover': onClick
          ? {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 20px ${alpha(colors.main, 0.15)}`,
              borderColor: `${alpha(colors.main, 0.4)}`,
            }
          : {},
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {/* Title and Value */}
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                fontWeight: 500,
                mb: 1,
              }}
            >
              {title}
            </Typography>

            {loading ? (
              <Box sx={{ height: '40px', bg: '#f3f4f6', borderRadius: 1 }} />
            ) : (
              <Typography
                sx={{
                  fontSize: '1.875rem',
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: subtitle ? 0.5 : 0,
                  lineHeight: 1,
                }}
              >
                {typeof value === 'number' && value > 999
                  ? (value / 1000).toFixed(1) + 'K'
                  : value}
              </Typography>
            )}

            {subtitle && (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Icon */}
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: 1.5,
                backgroundColor: alpha(colors.main, 0.1),
                color: colors.main,
                ml: 1,
                '& svg': {
                  fontSize: '1.5rem',
                },
              }}
            >
              {icon}
            </Box>
          )}
        </Box>

        {/* Trend Indicator */}
        {trend !== undefined && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={trendDirection === 'up' ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`${trendDirection === 'up' ? '+' : '-'}${Math.abs(trend)}%`}
              size="small"
              sx={{
                backgroundColor:
                  trendDirection === 'up'
                    ? alpha('#10b981', 0.1)
                    : alpha('#ef4444', 0.1),
                color: trendDirection === 'up' ? '#10b981' : '#ef4444',
                fontWeight: 600,
                fontSize: '0.75rem',
                '& .MuiChip-icon': {
                  fontSize: '0.875rem',
                  marginLeft: '4px !important',
                  marginRight: '-4px',
                },
              }}
            />
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
              }}
            >
              vs last month
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
